import { ChangeDetectorRef, Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AdminService } from 'src/app/service/admin.service';
import { ExportService } from 'src/app/service/export.service';
import { ReviewsService } from 'src/app/service/reviews.service';
import { buildXlsxBlob } from 'src/app/utils/xlsx-export';
import { readXlsxSheet, XlsxReadError, XLSX_MAX_ROWS } from 'src/app/utils/xlsx-import';
import { isValidEmail, normalizeHeader, pluralKey, stripWrappingQuotes } from 'src/app/utils/sheet-import-helpers';
import { EVENT_ADMIN_ICONS } from '../../event-admin-icons';

/** Situação de uma linha (um par proposta↔avaliador) após o confronto. */
export enum ReviewRowStatus {
  /** Dá pra atribuir. */
  READY = 'ready',
  /** `ID JEMS` vazio ou não numérico. */
  INVALID_ID = 'invalid-id',
  /** E-mail ausente ou malformado. */
  INVALID_EMAIL = 'invalid-email',
  /** Submissão não existe OU é de outro evento. */
  SUBMISSION_NOT_FOUND = 'submission-not-found',
  /** Avaliador sem conta no JEMS. */
  REVIEWER_NOT_FOUND = 'reviewer-not-found',
  /** O avaliador é AUTOR desta submissão — o JEMS proíbe. */
  IS_AUTHOR = 'is-author',
  /** Mesmo par proposta↔avaliador repetido na planilha. */
  DUPLICATE = 'duplicate',
  /** A submissão já tem esse avaliador atribuído. */
  EXISTING = 'existing',
  /** Atribuída nesta sessão. Estado TERMINAL. */
  CREATED = 'created',
}

export interface ReviewRow {
  sheetLine: number;
  /** `ID JEMS` como veio (texto) e como número, quando válido. */
  rawId: string;
  submissionId: number;
  /** Título que veio na planilha — redundante, serve pra conferência. */
  sheetTitle: string;
  /** Título gravado no JEMS; divergência do da planilha é sinal de engano. */
  jemsTitle?: string;
  email: string;
  emailKey: string;
  reviewerName?: string;
  /** Avaliador achado por e-mail ALTERNATIVO, não pelo principal. */
  matchedBySecondary?: boolean;
  /** Avaliador é membro do comitê do evento. Fora dele é AVISO, não bloqueio:
   *  a linha continua atribuível, mas não vem pré-marcada — marcar é a
   *  confirmação do chair. */
  inCommittee?: boolean;
  status: ReviewRowStatus;
  selected: boolean;
  creating?: boolean;
  createError?: string;
}

// "Título da submissão", não "da proposta": quem vinha da planilha antiga lia
// "proposta" como se o título fosse a chave da atribuição, quando a chave é
// SEMPRE o `ID JEMS`. O "(conferência)" no cabeçalho diz o papel da coluna sem
// ocupar a célula de exemplo. A leitura não depende disso — `mapColumns` casa
// por palavra-chave (`titulo`/`title`), então planilhas com o nome antigo
// continuam sendo lidas.
const TEMPLATE_HEADERS = ['ID JEMS', 'Título da submissão (conferência)', 'Avaliador'];
const I18N = 'admin.event.reviews.reviews-upload';

@Component({
  selector: 'app-reviews-upload',
  templateUrl: './reviews-upload.component.html',
  styleUrls: ['./reviews-upload.component.scss']
})
export class ReviewsUploadComponent {
  readonly icons = EVENT_ADMIN_ICONS;
  readonly Status = ReviewRowStatus;
  readonly maxRows = XLSX_MAX_ROWS;
  readonly i18n = I18N;

  fileName: string = null;
  rows: ReviewRow[] = [];
  error: string = null;
  isLoading = false;
  isCreating = false;
  isDragging = false;

  constructor(
    private adminService: AdminService,
    private reviewsService: ReviewsService,
    private exportService: ExportService,
    private cdr: ChangeDetectorRef
  ) { }

  // ============================================================
  // Resumo
  // ============================================================

  get total(): number { return this.rows.length; }
  get readyCount(): number { return this.countOf(ReviewRowStatus.READY); }
  get existingCount(): number { return this.countOf(ReviewRowStatus.EXISTING); }
  get createdCount(): number { return this.countOf(ReviewRowStatus.CREATED); }
  get problemCount(): number {
    return this.countOf(ReviewRowStatus.INVALID_ID) + this.countOf(ReviewRowStatus.INVALID_EMAIL)
      + this.countOf(ReviewRowStatus.SUBMISSION_NOT_FOUND) + this.countOf(ReviewRowStatus.REVIEWER_NOT_FOUND)
      + this.countOf(ReviewRowStatus.IS_AUTHOR) + this.countOf(ReviewRowStatus.DUPLICATE);
  }
  /** Linhas atribuíveis cujo avaliador está FORA do comitê — o aviso que o
   *  chair precisa confirmar marcando. */
  get outsideCommitteeCount(): number {
    return this.rows.filter(r => r.status === ReviewRowStatus.READY && r.inCommittee === false).length;
  }

  private countOf(status: ReviewRowStatus): number {
    return this.rows.filter(r => r.status === status).length;
  }

  pluralKey(base: string, count: number): string { return pluralKey(base, count); }

  // ============================================================
  // Planilha modelo
  // ============================================================

  async downloadTemplate(): Promise<void> {
    // Títulos de verdade nas células: a versão anterior repetia o texto
    // "Título da proposta (só para conferência)" como se fosse o valor, e
    // leitores entenderam que o título é que casava a atribuição.
    // As duas linhas repetem o MESMO id de propósito — é assim que se
    // atribuem dois avaliadores à mesma submissão.
    const rows = [
      { values: ['33665', 'Escalonamento de tarefas em redes veiculares', 'maria.andrade@exemplo.br'] },
      { values: ['33665', 'Escalonamento de tarefas em redes veiculares', 'joao.silva@exemplo.br'] },
    ];
    const blob = await buildXlsxBlob('Avaliadores', TEMPLATE_HEADERS, rows, [], [12, 60, 38], [], [], [0]);
    this.exportService.downloadBlob('jems-modelo-atribuicao-revisoes.xlsx', blob);
  }

  // ============================================================
  // Upload
  // ============================================================

  onDragOver(event: DragEvent): void { event.preventDefault(); this.isDragging = true; }
  onDragLeave(event: DragEvent): void { event.preventDefault(); this.isDragging = false; }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const files = event.dataTransfer && event.dataTransfer.files;
    if (files && files.length) { this.handleFile(files[0]); }
  }

  onFileSelected(input: HTMLInputElement): void {
    if (input.files && input.files.length) { this.handleFile(input.files[0]); }
    input.value = '';
  }

  clear(): void {
    this.fileName = null;
    this.rows = [];
    this.error = null;
  }

  private async handleFile(file: File): Promise<void> {
    this.error = null;
    this.rows = [];
    this.fileName = file.name;
    this.isLoading = true;

    try {
      const sheet = await readXlsxSheet(file);
      // Local até o confronto terminar — a tabela só aparece pronta.
      const parsedRows = this.toRows(sheet);
      if (!parsedRows.length) {
        this.error = `${I18N}.errors.empty`;
        return;
      }
      await this.check(parsedRows);
      // Pré-marca só o que está pronto E dentro do comitê: linha de avaliador
      // externo exige o clique do chair, que é a confirmação combinada.
      parsedRows.filter(r => r.status === ReviewRowStatus.READY && r.inCommittee !== false)
        .forEach(r => r.selected = true);
      this.rows = parsedRows;
    } catch (e) {
      this.error = e instanceof XlsxReadError
        ? `${I18N}.errors.${e.code}`
        : `${I18N}.errors.read-failed`;
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  // ============================================================
  // Cabeçalho e linhas
  // ============================================================

  /**
   * Em que coluna está cada campo.
   *
   * O avaliador vem na coluna "Avaliador" (que conterá o e-mail), então a
   * palavra-chave cobre tanto `avaliador`/`reviewer` quanto `mail` — planilhas
   * diferentes chamam a mesma coisa de jeitos diferentes. O `ID JEMS` é
   * procurado por `id`; o título é OPCIONAL (serve só pra conferência).
   */
  private mapColumns(header: string[]): { id: number, title: number, email: number } {
    const map = { id: -1, title: -1, email: -1 };
    header.forEach((cell, index) => {
      const h = normalizeHeader(cell);
      if (!h) { return; }
      if (map.email < 0 && (h.indexOf('mail') >= 0 || h.indexOf('avaliador') >= 0 || h.indexOf('reviewer') >= 0)) {
        map.email = index;
      } else if (map.id < 0 && h.indexOf('id') >= 0) {
        map.id = index;
      } else if (map.title < 0 && (h.indexOf('titulo') >= 0 || h.indexOf('title') >= 0)) {
        map.title = index;
      }
    });
    return map;
  }

  private toRows(sheet: string[][]): ReviewRow[] {
    if (!sheet.length) { return []; }

    const columns = this.mapColumns(sheet[0]);
    if (columns.id < 0) { throw new XlsxReadError('no-id-column'); }
    if (columns.email < 0) { throw new XlsxReadError('no-reviewer-column'); }

    const rows: ReviewRow[] = [];
    const seen = new Set<string>();

    for (let i = 1; i < sheet.length; i++) {
      const cells = sheet[i];
      const at = (index: number) => (index >= 0 && cells[index] ? cells[index] : '');

      const rawId = at(columns.id);
      const email = stripWrappingQuotes(at(columns.email));
      const emailKey = email.toLowerCase();
      // O Excel às vezes entrega o id como número com decimais ("33665.0").
      const parsed = /^\d+$/.test(rawId.replace(/\.0+$/, '')) ? parseInt(rawId, 10) : NaN;
      const row: ReviewRow = {
        sheetLine: i + 1,
        rawId,
        submissionId: parsed,
        sheetTitle: at(columns.title),
        email, emailKey,
        status: ReviewRowStatus.READY,
        selected: false,
      };

      const pairKey = `${parsed}|${emailKey}`;
      if (!rawId || isNaN(parsed)) {
        row.status = ReviewRowStatus.INVALID_ID;
      } else if (!email || !isValidEmail(email)) {
        row.status = ReviewRowStatus.INVALID_EMAIL;
      } else if (seen.has(pairKey)) {
        // A duplicidade é do PAR: a mesma proposta aparece em várias linhas
        // (uma por avaliador), e isso é normal.
        row.status = ReviewRowStatus.DUPLICATE;
      } else {
        seen.add(pairKey);
      }

      rows.push(row);
    }

    return rows;
  }

  // ============================================================
  // Seleção
  // ============================================================

  isSelectable(row: ReviewRow): boolean { return row.status === ReviewRowStatus.READY; }

  get selectableRows(): ReviewRow[] { return this.rows.filter(r => this.isSelectable(r)); }
  get selectedRows(): ReviewRow[] { return this.rows.filter(r => r.selected && this.isSelectable(r)); }
  get selectedCount(): number { return this.selectedRows.length; }

  allSelected(): boolean {
    const selectable = this.selectableRows;
    return selectable.length > 0 && selectable.every(r => r.selected);
  }

  someSelected(): boolean {
    const count = this.selectedCount;
    return count > 0 && count < this.selectableRows.length;
  }

  toggleAll(checked: boolean): void { this.selectableRows.forEach(r => r.selected = checked); }

  // ============================================================
  // Atribuição
  // ============================================================

  /**
   * Cria as atribuições marcadas — uma requisição por linha, em sequência,
   * cada resposta atualizando só a sua linha.
   *
   * Nasce em `ASSIGNED`, herda o prazo do evento e NÃO envia e-mail: o convite
   * é um passo separado, que o chair dispara quando quiser.
   */
  async createSelected(): Promise<void> {
    const toCreate = this.selectedRows;
    if (!toCreate.length || this.isCreating) { return; }

    this.isCreating = true;
    const eventId = this.adminService.eventId;

    for (const row of toCreate) {
      row.creating = true;
      row.createError = null;
      this.cdr.markForCheck();

      try {
        await this.reviewsService.bulkAssign(eventId, {
          submission: row.submissionId, email: row.email
        }).toPromise();
        row.status = ReviewRowStatus.CREATED;
        row.selected = false;
      } catch (e) {
        if (e instanceof HttpErrorResponse && e.status === 409) {
          row.status = ReviewRowStatus.EXISTING;
          row.selected = false;
        } else if (e instanceof HttpErrorResponse && e.status === 404) {
          row.status = ReviewRowStatus.REVIEWER_NOT_FOUND;
          row.selected = false;
        } else {
          row.createError = `${I18N}.errors.assign-failed`;
        }
      } finally {
        row.creating = false;
        this.cdr.markForCheck();
      }
    }

    this.isCreating = false;
    this.cdr.markForCheck();
  }

  // ============================================================
  // Confronto com a base
  // ============================================================

  /**
   * Uma ida só: quais avaliadores têm conta (e se são do comitê), quais
   * submissões existem NESTE evento, e o estado de cada par.
   */
  private check(rows: ReviewRow[]): Promise<void> {
    const pending = rows.filter(r => r.status === ReviewRowStatus.READY);
    if (!pending.length) { return Promise.resolve(); }

    const eventId = this.adminService.eventId;
    const payload = pending.map(r => ({ key: r.sheetLine, submission: r.submissionId, email: r.emailKey }));

    return new Promise(resolve => {
      this.reviewsService.bulkLookup(eventId, payload).subscribe(result => {
        const reviewers = new Map((result.reviewers || []).map(r => [r.requestedEmail, r]));
        const submissions = new Map((result.submissions || []).map(s => [s.id, s.title]));
        const states = new Map((result.rows || []).map(r => [r.key, r]));

        pending.forEach(row => {
          const reviewer = reviewers.get(row.emailKey);
          if (!submissions.has(row.submissionId)) {
            row.status = ReviewRowStatus.SUBMISSION_NOT_FOUND;
            return;
          }
          row.jemsTitle = submissions.get(row.submissionId);
          if (!reviewer) {
            row.status = ReviewRowStatus.REVIEWER_NOT_FOUND;
            return;
          }
          row.reviewerName = [reviewer.firstName, reviewer.lastName].filter(p => !!p).join(' ').trim() || null;
          row.matchedBySecondary = reviewer.matchedField !== 'email';
          row.inCommittee = reviewer.inCommittee;

          const state = states.get(row.sheetLine);
          if (state?.isAuthor) {
            row.status = ReviewRowStatus.IS_AUTHOR;
          } else if (state?.alreadyAssigned) {
            row.status = ReviewRowStatus.EXISTING;
          }
        });

        resolve();
      }, () => {
        // Sem o confronto não dá pra afirmar que alguma linha é atribuível.
        this.error = `${I18N}.errors.lookup-failed`;
        pending.forEach(row => row.status = ReviewRowStatus.SUBMISSION_NOT_FOUND);
        resolve();
      });
    });
  }
}
